import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { CuelloDeBotella, RecorridoOrden } from '@/lib/supabase/tipos-bd'

/**
 * El reloj por estación.
 *
 * Sale de la bitácora, no de columnas que alguien tenga que mantener: cada
 * cambio de estado quedó grabado con su instante y de ahí se reconstruye
 * cuánto estuvo el trabajo en cada sitio.
 */

/** Dónde se está atascando el trabajo. */
export function useCuelloDeBotella() {
  const consulta = useQuery({
    queryKey: ['cuello-de-botella'],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<CuelloDeBotella[]> => {
      const { data, error } = await supabase.rpc('fn_cuello_de_botella')

      if (error) throw new Error(mensajeDeError(error))

      return ((data ?? []) as unknown as CuelloDeBotella[]).map((fila) => ({
        ...fila,
        horas_mediana: fila.horas_mediana === null ? null : Number(fila.horas_mediana),
        horas_maxima: fila.horas_maxima === null ? null : Number(fila.horas_maxima),
      }))
    },
  })

  return { estaciones: consulta.data ?? [], cargando: consulta.isLoading }
}

/**
 * El recorrido de todas las órdenes, indexado por orden de servicio.
 *
 * Se pide entero y se reparte en memoria: la matriz pinta doce tarjetas de una
 * vez y doce consultas por tarjeta serían doce viajes para el mismo dato.
 */
export function useRecorridos() {
  const consulta = useQuery({
    queryKey: ['recorridos'],
    staleTime: 60 * 1000,
    // Johan: «¿por qué no se actualiza solo el tiempo que lleva ahí, por
    // ejemplo cada 5 min?». Esta consulta no se volvía a pedir nunca con la
    // página abierta. Ahora se repasa cada cinco minutos, se refresca al
    // instante cuando algo cambia (useOrdenesEnVivo) y, entre una y otra, la
    // matriz corre el reloj sola.
    refetchInterval: 5 * 60 * 1000,
    queryFn: async (): Promise<Map<string, RecorridoOrden>> => {
      const { data, error } = await supabase.from('vista_recorrido_orden').select('*')

      if (error) throw new Error(mensajeDeError(error))

      const filas = (data ?? []) as unknown as RecorridoOrden[]
      return new Map(filas.map((fila) => [fila.orden_servicio_id, fila]))
    },
  })

  return {
    recorridos: consulta.data ?? new Map<string, RecorridoOrden>(),
    /** Cuándo llegaron estas horas: desde ahí se sigue contando. */
    medidoEn: consulta.dataUpdatedAt,
  }
}

/** El recorrido de una sola orden, con su desglose por estación. */
export function useRecorrido(ordenServicioId: string | null) {
  const consulta = useQuery({
    queryKey: ['recorrido', ordenServicioId],
    enabled: Boolean(ordenServicioId),
    staleTime: 60 * 1000,
    queryFn: async (): Promise<RecorridoOrden | null> => {
      const { data, error } = await supabase
        .from('vista_recorrido_orden')
        .select('*')
        .eq('orden_servicio_id', ordenServicioId!)
        .maybeSingle()

      if (error) throw new Error(mensajeDeError(error))

      return (data ?? null) as unknown as RecorridoOrden | null
    },
  })

  return { recorrido: consulta.data ?? null, cargando: consulta.isLoading }
}
